import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Mui } from "@leap4/frontend";
import { demandBaseUrl } from "../../../../config/generals";
import { apiClient } from "@leap4/frontend";

const { Typography, List, ListItem, ListItemText, Paper, Chip, LinearProgress, CircularProgress, Box, TextField, InputAdornment } = Mui;

interface ActivePublisher {
    name: string;
    pubAccId: string;
}

interface ActivePublishersProps {
    demandId: string;
}

const SLOW_THRESHOLD_MS = 5000;

const fetchActivePublishers = async (demandId: string): Promise<ActivePublisher[]> => {
    const response = await apiClient.get(`${demandBaseUrl}/${demandId}/publishers`, {
        timeout: 120_000,
    });
    return response.data;
};

const ActivePublishers = ({ demandId }: ActivePublishersProps) => {
    const [isSlow, setIsSlow] = useState(false);
    const [filter, setFilter] = useState("");

    const { data: publishers, isLoading, isError, isFetching } = useQuery(
        ["activePublishers", demandId],
        () => fetchActivePublishers(demandId),
        {
            refetchOnWindowFocus: false,
            enabled: !!demandId,
            retry: 1,
            staleTime: 5 * 60 * 1000,
        }
    );

    useEffect(() => {
        if (!isFetching) {
            setIsSlow(false);
            return undefined;
        }
        const timer = setTimeout(() => setIsSlow(true), SLOW_THRESHOLD_MS);
        return () => clearTimeout(timer);
    }, [isFetching]);

    return (
        <Paper
            elevation={2}
            sx={{
                p: 2,
                mt: "35px",
                minWidth: 240,
                maxHeight: 650,
                overflow: "auto",
            }}
        >
            <Typography
                fontFamily="Quicksand"
                fontWeight={600}
                variant="subtitle1"
                sx={{ mb: 1 }}
            >
                ACTIVE PUBLISHERS
            </Typography>

            {isLoading && (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2, gap: 1.5 }}>
                    <CircularProgress size={28} />
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                        {isSlow ? "ClickHouse is waking up, this may take a moment..." : "Loading..."}
                    </Typography>
                    {isSlow && <LinearProgress sx={{ width: "100%", borderRadius: 1 }} />}
                </Box>
            )}

            {isError && (
                <Typography variant="body2" color="error">
                    Failed to load publishers
                </Typography>
            )}

            {!isLoading && !isError && publishers?.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    No active publishers found
                </Typography>
            )}

            {!isLoading && !isError && publishers && publishers.length > 0 && (() => {
                const filtered = filter
                    ? publishers.filter(p => p.name.toLowerCase().includes(filter.toLowerCase()))
                    : publishers;

                return (
                    <>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                            {publishers.length} publisher{publishers.length !== 1 ? "s" : ""} found
                        </Typography>
                        <TextField
                            size="small"
                            placeholder="Filter publishers..."
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            fullWidth
                            sx={{ mb: 1 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Typography variant="body2">🔍</Typography>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {filtered.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                                No matches for &quot;{filter}&quot;
                            </Typography>
                        )}
                        <List dense disablePadding>
                            {filtered.map((pub, idx) => (
                                <ListItem key={`${pub.name}-${pub.pubAccId}-${idx}`} disableGutters sx={{ py: 0.5 }}>
                                    <Chip
                                        size="small"
                                        sx={{
                                            bgcolor: "success.main",
                                            width: 10,
                                            height: 10,
                                            mr: 1.5,
                                            minWidth: 10,
                                            "& .MuiChip-label": { display: "none" },
                                        }}
                                    />
                                    <ListItemText
                                        primary={pub.name}
                                        secondary={`Acc: ${pub.pubAccId}`}
                                        primaryTypographyProps={{ variant: "body2" }}
                                        secondaryTypographyProps={{ variant: "caption" }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </>
                );
            })()}
        </Paper>
    );
};

export { ActivePublishers };
